import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import {
  Bug, Radio, CloudSun, Loader2, RefreshCw, Info, MapPin, ArrowLeft,
  AlertTriangle, CalendarClock, Sprout, Droplets, SlidersHorizontal,
} from 'lucide-react';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import pestModelService from '../../services/pestModels';
import orchardService from '../../services/orchard';
import type { PestOverviewItem, PestRiskSeries, PestRiskDay, PestModel } from '../../types/pest';
import { INFECTION_MODEL_TYPES } from '../../types/pest';
import PestProtectionPanel from '../../components/spray/PestProtectionPanel';
import { riskColor, riskLabel, riskBadgeClass, plotMetaFor, plotValue, KIND_LABEL } from '../../utils/pestRisk';
import '../../styles/GlobalPageStyles.css';

Chart.register(...registerables);

// -----------------------------------------------------------------------------
// Risk chart — accumulation/score line, points coloured by that day's risk,
// forecast days rendered with a dashed segment.
// -----------------------------------------------------------------------------
function RiskChart({ series, modelType, tz }: { series: PestRiskDay[]; modelType: PestRiskSeries['meta']['model_type']; tz: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    if (chartRef.current) chartRef.current.destroy();

    const isMobile = window.innerWidth < 640;
    const meta = plotMetaFor(modelType);
    const labels = series.map((d) => formatLocalDateInTz(d.local_date, tz, { month: 'short', day: 'numeric' }));
    const firstForecast = series.findIndex((d) => d.is_forecast);

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: meta.label,
          data: series.map((d) => plotValue(modelType, d)),
          borderColor: '#0f766e',
          backgroundColor: '#0f766e18',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          pointRadius: series.map((d) => (d.risk_level !== 'none' ? 3 : 0)),
          pointBackgroundColor: series.map((d) => riskColor(d.risk_level)),
          pointBorderColor: series.map((d) => riskColor(d.risk_level)),
          segment: {
            borderDash: (c) => (firstForecast >= 0 && c.p1DataIndex >= firstForecast ? [5, 4] : undefined),
          },
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: { ticks: { display: !isMobile, maxTicksLimit: 8, autoSkip: true } },
          y: { beginAtZero: true, title: { display: !isMobile && !!meta.unit, text: meta.unit } },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              afterLabel: (item) => {
                const d = series[item.dataIndex];
                const parts = [`Risk: ${riskLabel(d.risk_level)}`];
                if (d.is_forecast) parts.push('(forecast)');
                if (d.events?.length) parts.push(...d.events.map((e) => `• ${e.name}`));
                return parts.join('\n');
              },
            },
          },
        },
      },
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [series, modelType, tz]);

  return <div className="h-72"><canvas ref={canvasRef} /></div>;
}

// -----------------------------------------------------------------------------
// PestRiskDashboard
// -----------------------------------------------------------------------------
export default function PestRiskDashboard() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [searchParams, setSearchParams] = useSearchParams();

  const [overview, setOverview] = useState<PestOverviewItem[]>([]);
  const [allPests, setAllPests] = useState<PestModel[]>([]);
  const [orgFarms, setOrgFarms] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [selectedPestId, setSelectedPestId] = useState<number | null>(null);
  const [series, setSeries] = useState<PestRiskSeries | null>(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const farmParam = searchParams.get('farm');
  const pestParam = searchParams.get('pest');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOverview(true);
      // Overview = auto-monitored (pest × farm) pairs only. The full model and
      // farm lists let the selector reach any pest on any farm — risk for an
      // unmonitored pair is computed on demand by the /risk endpoint.
      const [items, models, farmRes] = await Promise.all([
        pestModelService.getOverview(),
        pestModelService.listModels(true).catch(() => [] as PestModel[]),
        orchardService.getFarms(1, 200).catch(() => null),
      ]);
      if (cancelled) return;
      setOverview(items);
      const pests = models;
      setAllPests(pests);

      const fetched = farmRes?.success && farmRes.data
        ? farmRes.data.farms.map((f) => ({ id: f.id, name: f.name }))
        : [];
      const farmList = fetched.length ? fetched : farmsFromOverview(items);
      setOrgFarms(fetched);

      if (selectedFarmId == null && farmList.length) {
        const paramFarm = farmParam ? Number(farmParam) : null;
        // Prefer the URL-selected farm, else the highest-risk one, else first.
        const ranked = [...items].sort((a, b) =>
          (b.summary ? riskRankOf(b) : -1) - (a.summary ? riskRankOf(a) : -1));
        const farmId = (paramFarm != null && farmList.some((f) => f.id === paramFarm))
          ? paramFarm
          : ranked[0]?.farm_id ?? farmList[0].id;
        setSelectedFarmId(farmId);

        const paramPest = pestParam ? Number(pestParam) : null;
        setSelectedPestId(
          (paramPest != null && pests.some((p) => p.id === paramPest)) ? paramPest
            : items.find((i) => i.farm_id === farmId)?.pest_model_id ?? pests[0]?.id ?? null,
        );
      }
      setLoadingOverview(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const farms = useMemo(
    () => (orgFarms.length ? orgFarms : farmsFromOverview(overview)),
    [orgFarms, overview],
  );

  // Pests auto-monitored on the selected farm (they have overview summaries)…
  const pestsForFarm = useMemo(() => {
    const rows = overview.filter((i) => i.farm_id === selectedFarmId);
    if (!allPests.length) return rows;
    const ids = new Set(allPests.map((p) => p.id));
    return rows.filter((i) => ids.has(i.pest_model_id));
  }, [overview, selectedFarmId, allPests]);

  // …and everything else the org can look at, computed on demand when selected.
  const otherPests = useMemo(() => {
    const monitored = new Set(pestsForFarm.map((i) => i.pest_model_id));
    return allPests.filter((p) => !monitored.has(p.id));
  }, [allPests, pestsForFarm]);

  const loadSeries = useCallback(async (refresh = false) => {
    if (selectedFarmId == null || selectedPestId == null) return;
    if (refresh) setRefreshing(true); else setLoadingSeries(true);
    const data = await pestModelService.getRisk(selectedFarmId, { pestModelId: selectedPestId, refresh });
    setSeries(data);
    setRefreshing(false);
    setLoadingSeries(false);
  }, [selectedFarmId, selectedPestId]);

  useEffect(() => { loadSeries(false); }, [loadSeries]);

  const selectedPest = useMemo(
    () => allPests.find((p) => p.id === selectedPestId) || null,
    [allPests, selectedPestId],
  );
  const selectedIsMonitored = pestsForFarm.some((i) => i.pest_model_id === selectedPestId);

  const setUrl = (farmId: number, pestId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('farm', String(farmId));
    next.set('pest', String(pestId));
    setSearchParams(next, { replace: true });
  };

  const onSelectFarm = (id: number) => {
    setSelectedFarmId(id);
    setSeries(null);
    // Keep the current pest if it's still selectable, otherwise fall back to a
    // pest monitored on the new farm, then to any active pest.
    const keep = selectedPestId != null && allPests.some((p) => p.id === selectedPestId);
    const pestId = keep
      ? selectedPestId
      : overview.find((i) => i.farm_id === id)?.pest_model_id ?? allPests[0]?.id ?? null;
    setSelectedPestId(pestId);
    if (pestId != null) setUrl(id, pestId);
  };
  const onSelectPest = (id: number) => {
    setSelectedPestId(id);
    setSeries(null);
    if (selectedFarmId != null) setUrl(selectedFarmId, id);
  };

  if (loadingOverview && !overview.length) {
    return (
      <>
        <PageHeader title="Pest & disease risk" />
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-2 text-neutral-500 mt-6">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        </div>
      </>
    );
  }

  if (!farms.length || !allPests.length) {
    return (
      <>
        <PageHeader title="Pest & disease risk" subtitle="Weather-driven pest & disease forecasting" />
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <Card>
            <CardContent className="py-10 text-center text-neutral-500">
              <Bug className="h-8 w-8 mx-auto mb-3 text-neutral-300" />
              {!farms.length ? (
                <>
                  <p>No farms to forecast against yet.</p>
                  <p className="text-sm mt-1">
                    Add a farm in{' '}
                    <button className="text-primary-600 underline" onClick={() => navigate('/orchard')}>Farms</button>,
                    then come back here.
                  </p>
                </>
              ) : (
                <>
                  <p>No pests or diseases are defined yet.</p>
                  <p className="text-sm mt-1">
                    A spray admin can define pests and their hosts in{' '}
                    <button className="text-primary-600 underline" onClick={() => navigate('/spray/pests')}>Spray → Pests &amp; Diseases</button>.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const summary = series?.summary;
  const meta = series?.meta;

  return (
    <>
      <PageHeader
        title="Pest & disease risk"
        subtitle={meta ? `${meta.pest_name} · season ${meta.season_year}` : 'Weather-driven pest & disease forecasting'}
      />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/weather')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
          >
            <ArrowLeft className="h-4 w-4" /> Weather
          </button>

          <label className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-neutral-500" />
            <select
              className="rounded-lg border border-neutral-200 px-3 py-2 text-sm"
              value={selectedFarmId ?? ''}
              onChange={(e) => onSelectFarm(Number(e.target.value))}
            >
              {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Bug className="h-4 w-4 text-neutral-500" />
            <select
              className="rounded-lg border border-neutral-200 px-3 py-2 text-sm"
              value={selectedPestId ?? ''}
              onChange={(e) => onSelectPest(Number(e.target.value))}
            >
              {pestsForFarm.length > 0 && (
                <optgroup label="Monitored on this farm">
                  {pestsForFarm.map((i) => (
                    <option key={i.pest_model_id} value={i.pest_model_id}>{i.pest_name}</option>
                  ))}
                </optgroup>
              )}
              {otherPests.length > 0 && (
                <optgroup label={pestsForFarm.length ? 'Other pests & diseases' : 'All pests & diseases'}>
                  {otherPests.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>

          <button
            onClick={() => navigate('/spray/pests')}
            title="Create or edit pests, hosts, models and alert rules"
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
          >
            <SlidersHorizontal className="h-4 w-4" /> Manage pests
          </button>
          <button
            onClick={() => navigate(selectedFarmId != null ? `/spray/new?farm_id=${selectedFarmId}` : '/spray/new')}
            title="Log a spray application in response to this risk"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary-50 text-primary-700 hover:bg-primary-100"
          >
            <Droplets className="h-4 w-4" /> Log a spray
          </button>
          <button
            onClick={() => loadSeries(true)}
            disabled={refreshing || selectedPestId == null}
            title="Recompute from the latest weather + forecast"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Source badges */}
        {meta && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {meta.source === 'station' || meta.source === 'mixed' ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                <Radio className="h-3.5 w-3.5" /> On-farm station{meta.station_name ? ` · ${meta.station_name}` : ''}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">
                <CloudSun className="h-3.5 w-3.5" /> Modelled from Open-Meteo
              </span>
            )}
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700">
              <Info className="h-3.5 w-3.5" /> Forecast leaf wetness modelled from humidity &amp; rain
            </span>
            {meta.through_date && (
              <span className="text-neutral-500">
                observed through {formatLocalDateInTz(meta.through_date, orgTz, { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        )}

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl p-4 border border-neutral-200">
              <div className="text-xs font-medium text-neutral-500 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Current risk</div>
              <div className="mt-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-sm font-semibold ${riskBadgeClass(summary.current_level)}`}>
                  {riskLabel(summary.current_level)}
                </span>
              </div>
              {summary.current_value != null && (
                <div className="text-xs text-neutral-500 mt-2">{summary.current_value} {plotMetaFor(meta!.model_type).unit} to date</div>
              )}
            </div>

            <div className="rounded-xl p-4 border border-neutral-200">
              <div className="text-xs font-medium text-neutral-500 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Forecast peak ({series!.meta.forecast_end ? 'to ' + formatLocalDateInTz(series!.meta.forecast_end, orgTz, { day: 'numeric', month: 'short' }) : 'ahead'})</div>
              <div className="mt-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-sm font-semibold ${riskBadgeClass(summary.peak_forecast_level)}`}>
                  {riskLabel(summary.peak_forecast_level)}
                </span>
              </div>
              {summary.peak_forecast_date && (
                <div className="text-xs text-neutral-500 mt-2">on {formatLocalDateInTz(summary.peak_forecast_date, orgTz, { day: 'numeric', month: 'short' })}</div>
              )}
            </div>

            <div className="rounded-xl p-4 border border-neutral-200">
              <div className="text-xs font-medium text-neutral-500 flex items-center gap-1.5"><Sprout className="h-4 w-4" /> Next event</div>
              {summary.next_event ? (
                <>
                  <div className="mt-2 text-sm font-semibold text-neutral-800">{summary.next_event.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {summary.next_event.projected_date
                      ? `~${formatLocalDateInTz(summary.next_event.projected_date, orgTz, { day: 'numeric', month: 'short' })}`
                      : `at ${summary.next_event.dd} °Cd`}
                  </div>
                  {summary.next_event.action && <div className="text-xs text-neutral-500 mt-1">{summary.next_event.action}</div>}
                </>
              ) : (
                <div className="mt-2 text-sm text-neutral-400">—</div>
              )}
            </div>
          </div>
        )}

        {/* Wash-off and curative windows, for diseases with an infection model */}
        {selectedPest && selectedFarmId != null && INFECTION_MODEL_TYPES.includes(selectedPest.model_type) && (
          <PestProtectionPanel farmId={selectedFarmId} pestId={selectedPest.id} reloadKey={series} />
        )}

        {/* Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bug className="h-5 w-5 text-teal-600" />
              {meta ? plotMetaFor(meta.model_type).label : 'Risk'} · season to date + forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSeries && !series && (
              <div className="flex items-center gap-2 text-sm text-neutral-500 h-72">
                <Loader2 className="h-4 w-4 animate-spin" /> Computing risk…
              </div>
            )}
            {!loadingSeries && series && series.series.length === 0 && (
              <div className="text-sm text-neutral-500 h-72 flex items-center">
                No data for this season yet — accumulation starts on {series.meta.season_start}.
              </div>
            )}
            {series && series.series.length > 0 && meta && (
              <RiskChart series={series.series} modelType={meta.model_type} tz={orgTz} />
            )}
            {selectedPest && (
              <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
                Solid line = observed, dashed = forecast. Points are coloured by that day's risk level.
                {!selectedIsMonitored && (
                  <>
                    {' '}<span className="text-amber-700">
                      {selectedPest.name} isn't monitored on this farm — this is a one-off "what if" calculation and
                      won't raise alerts. Turn on monitoring under{' '}
                      <button className="underline" onClick={() => navigate('/spray/pests')}>Manage pests</button>.
                    </span>
                  </>
                )}
              </p>
            )}
          </CardContent>
        </Card>

        {/* All pests for this farm */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">All pests · {farms.find((f) => f.id === selectedFarmId)?.name}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {pestsForFarm.length > 0 ? (
              <div>
                <div className="text-xs font-medium text-neutral-500 mb-2">Monitored on this farm</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {pestsForFarm.map((i) => (
                    <button
                      key={i.pest_model_id}
                      onClick={() => onSelectPest(i.pest_model_id)}
                      className={`text-left rounded-xl p-3 border ${
                      i.pest_model_id === selectedPestId ? 'border-neutral-800' : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                    >
                      <div className="text-sm font-semibold text-neutral-800 truncate">{i.pest_name}</div>
                      <div className="text-xs text-neutral-500 mb-1.5">{KIND_LABEL[i.kind] || i.kind}</div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${riskBadgeClass(i.summary?.current_level)}`}>
                        {riskLabel(i.summary?.current_level)}
                      </span>
                      {i.summary && i.summary.peak_forecast_level !== 'none' && i.summary.peak_forecast_level !== i.summary.current_level && (
                        <span className="ml-1 text-[11px] text-neutral-500">→ {riskLabel(i.summary.peak_forecast_level)}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-500">
                Nothing is auto-monitored on this farm yet — no planted cultivar matches a pest's host crop.
                You can still pick any pest below to see what its model says for this location.
              </p>
            )}

            {otherPests.length > 0 && (
              <div>
                <div className="text-xs font-medium text-neutral-500 mb-2">
                  Not monitored here · select to calculate on demand
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {otherPests.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => onSelectPest(p.id)}
                      className={`text-left rounded-xl p-3 border border-dashed ${
                      p.id === selectedPestId ? 'border-neutral-800' : 'border-neutral-300 hover:border-neutral-400'
                    }`}
                    >
                      <div className="text-sm font-semibold text-neutral-700 truncate">{p.name}</div>
                      <div className="text-xs text-neutral-500">{KIND_LABEL[p.kind] || p.kind}</div>
                      {p.host_crop_type_names?.length ? (
                        <div className="text-[11px] text-neutral-400 mt-1.5 truncate">
                          Hosts: {p.host_crop_type_names.join(', ')}
                        </div>
                      ) : (
                        <div className="text-[11px] text-neutral-400 mt-1.5">No host crops set</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function farmsFromOverview(items: PestOverviewItem[]): Array<{ id: number; name: string }> {
  const seen = new Map<number, string>();
  for (const i of items) if (!seen.has(i.farm_id)) seen.set(i.farm_id, i.farm_name);
  return Array.from(seen, ([id, name]) => ({ id, name }));
}

function riskRankOf(i: PestOverviewItem): number {
  const order = ['none', 'low', 'moderate', 'high', 'severe'];
  const cur = order.indexOf(i.summary?.current_level ?? 'none');
  const peak = order.indexOf(i.summary?.peak_forecast_level ?? 'none');
  return Math.max(cur, peak);
}
